import * as React from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  required,
  minValue,
  maxValue,
  FormDataConsumer,
  SelectInput,
  BooleanInput,
  ArrayInput,
  SimpleFormIterator,
  SelectArrayInput,
  useRecordContext,
} from "react-admin";
import { useWatch } from "react-hook-form";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";

/* ---------------- helpers ---------------- */

function joinCsv(arr?: any[]): string {
  return Array.isArray(arr) ? arr.join(", ") : "";
}

function parseCsv(input?: string): string[] {
  return (input ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseIntCsv(input?: string): number[] {
  return parseCsv(input)
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n))
    .map((n) => Math.trunc(n));
}

function makeColumnLabels(maxRounds: number) {
  const n = Math.max(1, Math.trunc(maxRounds || 1));
  return Array.from({ length: n }, (_, i) => `R${i + 1}`);
}

function clampInt(n: any, min: number, max: number, fallback: number) {
  const v = Number(n);
  if (!Number.isFinite(v)) return fallback;
  return Math.max(min, Math.min(max, Math.trunc(v)));
}

function sanitizeAllowed(arr: any): number[] {
  const raw = Array.isArray(arr) ? arr : [3, 2, 1];
  const cleaned = raw
    .map((x) => clampInt(x, 1, 3, 3))
    .filter((v, i, a) => a.indexOf(v) === i);
  return cleaned.length ? cleaned : [3, 2, 1];
}

function sanitizeX01Rules(data: any) {
  const rules: any[] = Array.isArray(data?.x01_rules?.darts_rules) ? data.x01_rules.darts_rules : [];
  const cleaned = rules.map((r) => {
    const mode = String(r?.mode ?? "CHOICE").toUpperCase();
    const from = clampInt(r?.from, 0, 999999, 501);
    const to = clampInt(r?.to, 0, 999999, 0);

    if (mode === "FIXED") {
      return {
        from,
        to,
        mode: "FIXED",
        show: false,
        fixed: clampInt(r?.fixed, 1, 3, 3),
      };
    }

    if (mode === "DEFAULT") {
      return {
        from,
        to,
        mode: "DEFAULT",
        show: Boolean(r?.show ?? true),
        allowed: sanitizeAllowed(r?.allowed),
        default: clampInt(r?.default, 1, 3, 3),
      };
    }

    return {
      from,
      to,
      mode: "CHOICE",
      show: Boolean(r?.show ?? true),
      allowed: sanitizeAllowed(r?.allowed),
    };
  });

  const qa = data?.x01_rules?.quick_actions ?? {};
  const quick_actions = {
    bust_button: Boolean(qa?.bust_button ?? true),
    from_remaining: clampInt(qa?.from_remaining, 0, 999999, 60),
  };

  return { cleanedRules: cleaned, quick_actions };
}

/* ---------------- Preview ---------------- */

function TemplatePreviewButton() {
  const [open, setOpen] = React.useState(false);

  const mode = (useWatch({ name: "input_mode" }) as "choices" | "numeric" | undefined) ?? "choices";
  const x01Enabled = Boolean(useWatch({ name: "x01_enabled" })) && mode === "numeric";

  const rowLabelsCsv = useWatch({ name: "rowLabelsCsv" }) as string | undefined;
  const columnLabelsCsv = useWatch({ name: "columnLabelsCsv" }) as string | undefined;

  const x01MaxRounds = (useWatch({ name: "x01_max_rounds" }) as number | undefined) ?? 40;
  const x01Start = (useWatch({ name: "x01_start_value" }) as number | undefined) ?? 501;

  const allowedScoresCsv = useWatch({ name: "allowedScoresCsv" }) as string | undefined;
  const numericMin = (useWatch({ name: "numeric_min" }) as number | undefined) ?? 0;
  const numericMax = (useWatch({ name: "numeric_max" }) as number | undefined) ?? 180;
  const dartsPerEntry = (useWatch({ name: "darts_per_entry" }) as number | undefined) ?? 3;

  const rowLabels = React.useMemo(() => {
    if (x01Enabled && mode === "numeric") return ["Runde"];
    return parseCsv(rowLabelsCsv);
  }, [x01Enabled, mode, rowLabelsCsv]);

  const colLabels = React.useMemo(() => {
    if (x01Enabled && mode === "numeric") return makeColumnLabels(x01MaxRounds);
    return parseCsv(columnLabelsCsv);
  }, [x01Enabled, mode, columnLabelsCsv, x01MaxRounds]);

  const rows = rowLabels.length;
  const cols = colLabels.length;
  const previewOk = rows > 0 && cols > 0;

  const allowedScores = React.useMemo(() => {
    const a = parseIntCsv(allowedScoresCsv).filter((n) => n >= 0);
    return a.length ? a : [0, 1, 2, 3];
  }, [allowedScoresCsv]);

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="outlined" sx={{ borderRadius: 3 }} onClick={() => setOpen(true)} disabled={!previewOk}>
          Vorschau
        </Button>

        <Chip
          size="small"
          variant="outlined"
          label={previewOk ? `${rows} Reihen × ${cols} Spalten` : "Reihen & Spalten angeben"}
        />

        {x01Enabled && mode === "numeric" && (
          <Chip size="small" variant="outlined" label={`X01: ${x01Start} / ${x01MaxRounds} Runden`} />
        )}

        {mode === "numeric" && (
          <Chip size="small" variant="outlined" label={`Range: ${numericMin}–${numericMax} / darts=${dartsPerEntry}`} />
        )}
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Vorschau</DialogTitle>

        <DialogContent dividers>
          {!previewOk ? (
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Bitte Reihen- und Spalten-Labels angeben.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                <Chip size="small" label={`Mode: ${mode}${x01Enabled ? " (X01)" : ""}`} />
                {mode === "choices" ? (
                  <Chip size="small" label={`Scores: ${allowedScores.join(", ")}`} />
                ) : (
                  <>
                    <Chip size="small" label={`Range: ${numericMin}–${numericMax}`} />
                    <Chip size="small" label={`${dartsPerEntry} Darts/Eingabe`} />
                  </>
                )}
              </Stack>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>Reihe \ Spalte</TableCell>
                      {colLabels.map((c, idx) => (
                        <TableCell key={idx} align="center" sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
                          {c}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rowLabels.map((r, rIdx) => (
                      <TableRow key={rIdx} hover>
                        <TableCell sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{r}</TableCell>
                        {colLabels.map((_, cIdx) => (
                          <TableCell key={cIdx} align="center" sx={{ opacity: 0.35 }}>
                            —
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Vorschau zeigt nur die Struktur (Reihen/Spalten).
              </Typography>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)} variant="contained" sx={{ borderRadius: 3 }}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ---------------- Inner Form ---------------- */

function TrainingTemplateEditForm() {
  const record = useRecordContext<any>();

  const defaultValues = React.useMemo(() => {
    if (!record) return {};

    const x01Enabled = Boolean(record?.x01_rules);
    const inferredMaxRounds =
      Array.isArray(record?.column_labels) && record.column_labels.length > 0
        ? record.column_labels.length
        : 40;

    return {
      ...record,

      rowLabelsCsv: joinCsv(record.row_labels),
      columnLabelsCsv: joinCsv(record.column_labels),
      quoteCheckpointsCsv: joinCsv(record.quote_checkpoints),
      allowedScoresCsv: joinCsv(record.allowed_scores),

      input_mode: record.input_mode ?? "choices",
      numeric_min: record.numeric_min ?? 0,
      numeric_max: record.numeric_max ?? 180,
      darts_per_entry: record.darts_per_entry ?? 3,

      x01_enabled: x01Enabled,
      x01_start_value: record?.x01_rules?.start_value ?? 501,
      x01_max_rounds: inferredMaxRounds,

      x01_rules:
        record?.x01_rules ??
        ({
          darts_rules: [],
          quick_actions: { from_remaining: 60, bust_button: true },
          bust_on_one: true,
          bust_on_negative: true,
          start_value: 501,
        } as any),
    };
  }, [record]);

  return (
    <SimpleForm key={record?.id ?? "loading"} defaultValues={defaultValues}>
      <TextInput source="name" label="Spielname" validate={[required()]} fullWidth />
      <TextInput source="description" label="Beschreibung" fullWidth multiline />

      <SelectInput
        source="input_mode"
        label="Eingabe-Modus"
        choices={[
          { id: "choices", name: "Buttons (choices)" },
          { id: "numeric", name: "Wert-Eingabe (numeric)" },
        ]}
        defaultValue="choices"
        fullWidth
      />

      <FormDataConsumer>
        {({ formData }) => {
          const mode = (formData.input_mode ?? "choices") as "choices" | "numeric";
          return mode === "numeric" ? (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <BooleanInput source="x01_enabled" label="X01 Countdown aktivieren (501/301…)" />
            </Stack>
          ) : null;
        }}
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) => {
          const mode = (formData.input_mode ?? "choices") as "choices" | "numeric";
          const x01Enabled = Boolean(formData.x01_enabled) && mode === "numeric";

          if (x01Enabled) {
            return (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <NumberInput
                  source="x01_start_value"
                  label="Startwert (z.B. 501)"
                  defaultValue={501}
                  validate={[required(), minValue(1)]}
                />
                <NumberInput
                  source="x01_max_rounds"
                  label="Max Runden (columns)"
                  defaultValue={40}
                  validate={[required(), minValue(1), maxValue(200)]}
                />

                <Chip
                  size="small"
                  variant="outlined"
                  label="Hinweis: rows=1 wird automatisch gesetzt, Spaltenlabels werden generiert (R1..Rn)"
                />

                <Stack direction="row" spacing={2}>
                  <NumberInput source="numeric_min" label="Numeric Min" defaultValue={0} disabled />
                  <NumberInput source="numeric_max" label="Numeric Max" defaultValue={180} disabled />
                </Stack>

                {/* ✅ X01: max 3 */}
                <NumberInput
                  source="darts_per_entry"
                  label="darts_per_entry (Info/Averages)"
                  defaultValue={3}
                  validate={[minValue(1), maxValue(3)]}
                />

                <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                  Darts-Regeln (Remaining Ranges)
                </Typography>

                <ArrayInput source="x01_rules.darts_rules" label="">
                  <SimpleFormIterator inline>
                    <NumberInput source="from" label="from (inkl.)" validate={[required()]} />
                    <NumberInput source="to" label="to (inkl.)" validate={[required()]} />

                    <SelectInput
                      source="mode"
                      label="Mode"
                      choices={[
                        { id: "CHOICE", name: "CHOICE (Buttons)" },
                        { id: "FIXED", name: "FIXED (auto)" },
                        { id: "DEFAULT", name: "DEFAULT (preselect + change)" },
                      ]}
                      defaultValue="CHOICE"
                    />

                    <FormDataConsumer>
  {({ scopedFormData }) => {
    const rm = String(scopedFormData?.mode ?? "CHOICE").toUpperCase();

    if (rm === "FIXED") {
      return (
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <NumberInput
            source="fixed"
            label="fixed (1–3)"
            defaultValue={3}
            validate={[required(), minValue(1), maxValue(3)]}
          />
        </Stack>
      );
    }

    if (rm === "DEFAULT") {
      return (
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <BooleanInput source="show" label="Buttons zeigen" defaultValue={true} />

          <SelectArrayInput
            source="allowed"
            label="allowed (1–3)"
            choices={[
              { id: 1, name: "1" },
              { id: 2, name: "2" },
              { id: 3, name: "3" },
            ]}
          />

          <NumberInput
            source="default"
            label="default (1–3)"
            defaultValue={3}
            validate={[required(), minValue(1), maxValue(3)]}
          />
        </Stack>
      );
    }

    // CHOICE
    return (
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <BooleanInput source="show" label="Buttons zeigen" defaultValue={true} />

        <SelectArrayInput
          source="allowed"
          label="allowed (1–3)"
          choices={[
            { id: 1, name: "1" },
            { id: 2, name: "2" },
            { id: 3, name: "3" },
          ]}
        />
      </Stack>
    );
  }}
</FormDataConsumer>

                  </SimpleFormIterator>
                </ArrayInput>

                <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                  Quick Actions
                </Typography>

                <Stack direction="row" spacing={2}>
                  <NumberInput
                    source="x01_rules.quick_actions.from_remaining"
                    label="Bust-Button ab Remaining ≤"
                    defaultValue={60}
                    validate={[minValue(0), maxValue(9999)]}
                  />
                  <BooleanInput
                    source="x01_rules.quick_actions.bust_button"
                    label="Bust Button aktiv"
                    defaultValue={true}
                  />
                </Stack>

                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  DEFAULT = vorselektiert, aber änderbar (allowed). FIXED = nicht änderbar.
                </Typography>
              </Stack>
            );
          }

          return (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextInput
                source="rowLabelsCsv"
                label="Reihen (Komma-Liste)"
                fullWidth
                validate={[required()]}
                helperText='z.B. "R1,R2,R3"'
              />
              <TextInput
                source="columnLabelsCsv"
                label="Spalten (Komma-Liste)"
                fullWidth
                validate={[required()]}
                helperText='z.B. "C1,C2,C3,C4"'
              />

              {mode === "choices" ? (
                <TextInput
                  source="allowedScoresCsv"
                  label="Erlaubte Werte (Komma-Liste)"
                  fullWidth
                  helperText='z.B. "0,1,2,3"'
                />
              ) : (
                <Stack direction="row" spacing={2}>
                  <NumberInput source="numeric_min" label="Numeric Min" defaultValue={0} />
                  <NumberInput source="numeric_max" label="Numeric Max" defaultValue={180} />
                  <NumberInput source="darts_per_entry" label="darts_per_entry" defaultValue={3} validate={[minValue(1), maxValue(9)]} />
                </Stack>
              )}

              <TextInput
                source="quoteCheckpointsCsv"
                label="Checkpoints (Komma-Liste)"
                fullWidth
                helperText='z.B. "3,6,10"'
              />
            </Stack>
          );
        }}
      </FormDataConsumer>

      <TemplatePreviewButton />
    </SimpleForm>
  );
}

/* ---------------- Edit ---------------- */

export function TrainingTemplateEdit() {
  return (
    <Edit
      transform={(data: any) => {
        const mode = (data.input_mode ?? "choices") as "choices" | "numeric";
        const x01Enabled = Boolean(data.x01_enabled) && mode === "numeric";

        const cps = parseIntCsv(data.quoteCheckpointsCsv)
          .map((n) => Math.trunc(n))
          .filter((n) => n > 0);

        const allowedScores = parseIntCsv(data.allowedScoresCsv)
          .map((n) => Math.trunc(n))
          .filter((n) => n >= 0);

        let dartsPerEntry = Math.trunc(Number(data.darts_per_entry ?? 3));
        const numericMin = Math.trunc(Number(data.numeric_min ?? 0));
        const numericMax = Math.trunc(Number(data.numeric_max ?? 180));

        let rowLabels: string[] = [];
        let colLabels: string[] = [];

        if (x01Enabled) {
          const maxRounds = Math.max(1, Math.trunc(Number(data.x01_max_rounds ?? 40)));
          rowLabels = ["Runde"];
          colLabels = makeColumnLabels(maxRounds);

          // ✅ X01: darts_per_entry auf max 3
          dartsPerEntry = clampInt(dartsPerEntry, 1, 3, 3);
        } else {
          rowLabels = parseCsv(data.rowLabelsCsv);
          colLabels = parseCsv(data.columnLabelsCsv);
          if (!rowLabels.length || !colLabels.length) {
            throw new Error("Reihen- und Spalten-Labels sind Pflicht.");
          }
        }

        const {
          rowLabelsCsv,
          columnLabelsCsv,
          quoteCheckpointsCsv,
          allowedScoresCsv,
          x01_enabled,
          x01_start_value,
          x01_max_rounds,
          ...rest
        } = data;

        const out: any = {
          ...rest,

          rows: rowLabels.length,
          columns: colLabels.length,

          row_labels: rowLabels,
          column_labels: colLabels,

          quote_checkpoints: cps,
          input_mode: mode,

          darts_per_entry:
            mode === "numeric"
              ? Number.isFinite(dartsPerEntry) && dartsPerEntry > 0
                ? dartsPerEntry
                : 3
              : null,

          allowed_scores:
            mode === "choices"
              ? allowedScores.length
                ? allowedScores
                : [0, 1, 2, 3]
              : null,

          numeric_min: mode === "numeric" ? numericMin : null,
          numeric_max: mode === "numeric" ? numericMax : null,
        };

        if (x01Enabled) {
          const { cleanedRules, quick_actions } = sanitizeX01Rules(data);

          out.x01_rules = {
            start_value: Math.max(1, Math.trunc(Number(x01_start_value ?? 501))),
            bust_on_one: true,
            bust_on_negative: true,
            darts_rules: cleanedRules,
            quick_actions,
          };

          out.numeric_min = 0;
          out.numeric_max = 180;
        } else {
          out.x01_rules = null;
        }

        return out;
      }}
    >
      <TrainingTemplateEditForm />
    </Edit>
  );
}
